import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { transactionsRepository } from '../repositories/transactions.repository'
import { paymentsRepository } from '../repositories/payments.repository'
import { useMutationTracker } from '../hooks/useMutationTracker'
import type { TransactionFilters, CompletePurchaseRequest, CancelPurchaseRequest, WithdrawalRequest } from '../models/transaction'

const TRANSACTIONS_KEY = 'transactions'
const TRANSACTION_KEY = 'transaction'
const COMPLETE_PURCHASE_KEY = 'complete-purchase'
const CANCEL_PURCHASE_KEY = 'cancel-purchase'
const REQUEST_WITHDRAWAL_KEY = 'request-withdrawal'

export const useTransactions = (filters?: TransactionFilters) => {
  const queryClient = useQueryClient()

  // Get all transactions
  const {
    data: transactionsData,
    isLoading: dataIsLoading,
    error: dataError,
    refetch
  } = useQuery({
    queryKey: [TRANSACTIONS_KEY, filters],
    queryFn: () => transactionsRepository.getAll(filters)
  })

  // Complete purchase mutation
  const completePurchaseMutation = useMutation({
    mutationKey: [COMPLETE_PURCHASE_KEY],
    mutationFn: (data: CompletePurchaseRequest) => paymentsRepository.completePurchase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['services'] })
      queryClient.invalidateQueries({ queryKey: ['user-subscriptions'] })
    }
  })

  // Cancel purchase mutation
  const cancelPurchaseMutation = useMutation({
    mutationKey: [CANCEL_PURCHASE_KEY],
    mutationFn: (data: CancelPurchaseRequest) => paymentsRepository.cancelPurchase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_KEY] })
    }
  })

  // Request withdrawal mutation
  const requestWithdrawalMutation = useMutation({
    mutationKey: [REQUEST_WITHDRAWAL_KEY],
    mutationFn: (data: WithdrawalRequest) => paymentsRepository.requestWithdrawal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
    }
  })

  // Track mutation states
  const completePurchaseState = useMutationTracker(COMPLETE_PURCHASE_KEY)
  const cancelPurchaseState = useMutationTracker(CANCEL_PURCHASE_KEY)
  const requestWithdrawalState = useMutationTracker(REQUEST_WITHDRAWAL_KEY)

  return {
    // Data
    transactions: transactionsData?.data || [],
    pagination: transactionsData?.pagination,
    
    // Loading and error states
    dataIsLoading,
    dataError,
    
    // Actions
    completePurchase: completePurchaseMutation.mutate,
    cancelPurchase: cancelPurchaseMutation.mutate,
    requestWithdrawal: requestWithdrawalMutation.mutate,
    refetch,
    
    // Mutation states
    completePurchaseLoading: completePurchaseState.isPending,
    completePurchaseError: completePurchaseState.error,
    completePurchaseSuccess: completePurchaseState.isSuccess,
    
    cancelPurchaseLoading: cancelPurchaseState.isPending,
    cancelPurchaseError: cancelPurchaseState.error,
    cancelPurchaseSuccess: cancelPurchaseState.isSuccess,
    
    requestWithdrawalLoading: requestWithdrawalState.isPending,
    requestWithdrawalError: requestWithdrawalState.error,
    requestWithdrawalSuccess: requestWithdrawalState.isSuccess
  }
}

export const useTransaction = (id: number) => {
  const {
    data: transaction,
    isLoading: dataIsLoading,
    error: dataError,
    refetch
  } = useQuery({
    queryKey: [TRANSACTION_KEY, id],
    queryFn: () => transactionsRepository.getById(id),
    enabled: !!id
  })

  return {
    transaction,
    dataIsLoading,
    dataError,
    refetch
  }
}